import { Alert, Card } from '@yoast/ui-library';
import { type ChartData, type ChartOptions, type Point } from 'chart.js';
import { Line } from 'react-chartjs-2';

import TextScore from '../../components/TextScore';
import TitleWithTooltip from '../../components/TitleWithTooltip';
import useScan from '../../hooks/useScan';
import { baseChartOptions } from '../../utils/chartSetup';

/**
 * Format a timestamp to 'Mon D' (e.g., Jan 4)
 */
function formatLabelDate(label: string | number): string {
  const date = new Date(label);
  if (isNaN(date.getTime())) return String(label);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * MentionsProgressCard component
 * @returns JSX.Element
 */
const MentionsProgressCard = () => {
  const { data: scan, isLoading, isRefetching } = useScan();

  const mentionsProgress = scan?.mentions;
  const mentionsPreviousDifference =
    mentionsProgress?.mentionedInNumberOfQueries?.previous?.difference;
  const chartData = mentionsProgress?.chart;
  const firstDataset = chartData?.datasets?.[0];

  // Normalize charts shapes to arrays to protect against unexpected API shapes
  const labelsArray: unknown[] = Array.isArray(chartData?.labels)
    ? (chartData!.labels as unknown[])
    : chartData?.labels && typeof chartData.labels === 'object'
      ? Object.values(chartData.labels as Record<string, unknown>)
      : [];

  // Format labels for chart display
  const formattedLabelsArray: string[] = labelsArray.map((label) =>
    formatLabelDate(String(label)),
  );

  // TASKS:
  // - [x] what are the upper and lower bounds for the chart?
  // - [x] can we show the chart if we only have two data points? if so how should it display?
  // - [ ] what is the max number of data points we can display?
  // - [ ] what if the data points are within the same date range, or more spread out such as 3 over one year?
  let maxYAxis = scan?.numberOfQueries;
  if (!maxYAxis) {
    let largestDataPoint = 0;

    // Iterate through all datasets and find the maximum value from their data arrays
    if (Array.isArray(chartData?.datasets)) {
      chartData.datasets.forEach((dataset) => {
        if (Array.isArray(dataset?.data)) {
          dataset.data.forEach((value) => {
            if (typeof value === 'number' && value > largestDataPoint) {
              largestDataPoint = value;
            }
          });
        }
      });
    }

    maxYAxis = largestDataPoint + 2;
  }

  const lineChartOptions: ChartOptions<'line'> = {
    ...baseChartOptions,
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        display: true,
        title: {
          display: false,
          text: 'Date',
        },
        grid: {
          display: false,
        },
      },
      y: {
        display: true,
        title: {
          display: false,
          text: 'Number of Mentions',
        },
        beginAtZero: true,
        // Extend Y-axis to show a bit more range above the data
        max: maxYAxis,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  return (
    <Card className="yst-overflow-visible">
      <Card.Content className="yst-space-y-4">
        <div className="yst-space-y-2">
          <TitleWithTooltip
            title="Mentions progress"
            // https://www.figma.com/design/zH43jZJuvZyPaqrod8lG5u?node-id=138-2053#1353633490
            tooltip="Tracks how your brand mentions have changed over time across the same query set."
          />

          <TextScore
            score={mentionsProgress?.mentionedInNumberOfQueries?.value}
            total={scan?.numberOfQueries}
            delta={mentionsPreviousDifference}
            deltaSuffix="since last analysis"
            suffix="queries"
            isLoading={isLoading || isRefetching}
          />
        </div>

        {/* Only show the alert when we can confirm <2 points — if the dataset */}
        {/* is absent (e.g., running against an older API), fall through to the */}
        {/* chart rather than misleading users with five analyses to "do more". */}
        {firstDataset && firstDataset.data.length < 2 ? (
          <Alert variant="info">
            One more analysis needed to show more insights.
          </Alert>
        ) : (
          <div className="yst-h-72">
            <Line
              data={
                {
                  labels: formattedLabelsArray,
                  datasets: chartData?.datasets ?? [],
                } as ChartData<'line', (number | Point | null)[], unknown>
              }
              options={lineChartOptions}
            />
          </div>
        )}
      </Card.Content>
    </Card>
  );
};

export default MentionsProgressCard;
